import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiRequestError } from '../../api/client';
import { Alert } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { storeLoginCredential } from '../../utils/credentials';

export function LoginPage() {
  const { login, loginDemo } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notVerified, setNotVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const demoAutoStarted = useRef(false);
  const accountDeleted = searchParams.get('deleted') === '1';

  async function handleDemoLogin() {
    setError('');
    setNotVerified(false);
    setDemoLoading(true);
    try {
      await loginDemo();
      navigate('/panel');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo abrir la demo');
    } finally {
      setDemoLoading(false);
    }
  }

  useEffect(() => {
    if (searchParams.get('demo') !== '1' || demoAutoStarted.current) return;
    demoAutoStarted.current = true;
    void handleDemoLogin();
  }, [searchParams]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setNotVerified(false);
    setLoading(true);

    const formData = new FormData(event.currentTarget);
    const submittedEmail = String(formData.get('username') ?? email).trim();
    const submittedPassword = String(formData.get('password') ?? password);

    try {
      await login(submittedEmail, submittedPassword);
      await storeLoginCredential(submittedEmail, submittedPassword);
      const redirect = searchParams.get('redirect');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/panel');
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'ACCOUNT_NOT_VERIFIED') {
        setNotVerified(true);
      }
      setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Iniciar sesión</h1>
        <p className="muted">Accede a tu panel cloud de TPV Hosty.</p>
        {accountDeleted ? (
          <div style={{ marginTop: '1rem' }}>
            <Alert tone="success">Tu cuenta ha sido eliminada permanentemente.</Alert>
          </div>
        ) : null}
        {error ? (
          <div style={{ marginTop: '1rem' }}>
            <Alert tone="danger">{error}</Alert>
            {notVerified ? (
              <p style={{ marginTop: '0.75rem' }}>
                <Link to={`/reenviar-verificacion?email=${encodeURIComponent(email)}`}>
                  Reenviar correo de verificación
                </Link>
              </p>
            ) : null}
          </div>
        ) : null}
        <form
          method="post"
          action="/login"
          autoComplete="on"
          onSubmit={handleSubmit}
        >
          <Input
            label="Email"
            id="username"
            name="username"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Contraseña"
            id="current-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" loading={loading}>
            Entrar
          </Button>
        </form>
        <div className="auth-demo">
          <p className="muted">¿Quieres ver el panel antes de registrarte?</p>
          <Button
            type="button"
            variant="secondary"
            loading={demoLoading}
            disabled={loading}
            onClick={handleDemoLogin}
          >
            Ver demo con datos de ejemplo
          </Button>
        </div>
        <p className="auth-card__footer">
          <Link to="/recuperar-contrasena">¿Olvidaste la contraseña?</Link>
          <br />
          ¿No tienes cuenta? <Link to="/registro">Regístrate</Link>
        </p>
      </div>
    </div>
  );
}
